import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Building2,
  User,
  Mail,
  Phone,
  MapPin,
  FileText,
  RefreshCw,
  Check,
  Copy,
  Eye,
  EyeOff,
  ArrowLeft,
  Loader2,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';

const COUNTRY_CODES = [
  { code: '+61', country: 'AU', flag: '🇦🇺' },
  { code: '+64', country: 'NZ', flag: '🇳🇿' },
  { code: '+1', country: 'US', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+353', country: 'IE', flag: '🇮🇪' },
  { code: '+65', country: 'SG', flag: '🇸🇬' },
  { code: '+91', country: 'IN', flag: '🇮🇳' },
  { code: '+49', country: 'DE', flag: '🇩🇪' },
  { code: '+33', country: 'FR', flag: '🇫🇷' },
  { code: '+971', country: 'AE', flag: '🇦🇪' },
  { code: '+966', country: 'SA', flag: '🇸🇦' },
  { code: '+27', country: 'ZA', flag: '🇿🇦' },
  { code: '+86', country: 'CN', flag: '🇨🇳' },
  { code: '+81', country: 'JP', flag: '🇯🇵' },
];

const TenantOnboarding = () => {
  const { isDark = true } = useOutletContext() || {};
  const navigate = useNavigate();
  const { api } = useSuperAdmin();
  
  // Form state
  const [formData, setFormData] = useState({
    // Tenant Info (Required)
    company_name: '',
    client_id: '',
    contact_email: '',
    contact_phone: '',
    // Tenant Info (Optional)
    address: '',
    abn: '',
    industry_type: '',
    // Subscription
    tier: 'basic',
    // Seats (total counts — bundled + extra)
    total_users: 1,
    total_mobile_workers: 2,
    // Admin User (Required)
    admin_full_name: '',
    admin_email: '',
    admin_phone: '',
    admin_password: '',
    // Options
    send_welcome_email: false
  });
  
  const [autoGeneratePassword, setAutoGeneratePassword] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [clientIdAuto, setClientIdAuto] = useState(true);
  const [clientIdValid, setClientIdValid] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [contactCountryCode, setContactCountryCode] = useState('+61');
  const [adminCountryCode, setAdminCountryCode] = useState('+61');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successModal, setSuccessModal] = useState(false);
  const [createdData, setCreatedData] = useState(null);
  const [copiedField, setCopiedField] = useState('');

  // Load industries on mount
  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        const res = await api.get('/api/super-admin/onboarding/industries');
        setIndustries(res.data.industries);
      } catch (err) {
        console.error('Failed to load industries:', err);
      }
    };
    fetchIndustries();
  }, [api]);

  // Generate client ID
  const generateClientId = async () => {
    try {
      const res = await api.get('/api/super-admin/onboarding/generate-client-id');
      setFormData(prev => ({ ...prev, client_id: res.data.client_id }));
      setClientIdValid(true);
    } catch (err) {
      console.error('Failed to generate client ID:', err);
    }
  };

  // Validate client ID
  const validateClientId = async (clientId) => {
    if (clientId.length !== 6) {
      setClientIdValid(false);
      return;
    }
    try {
      const res = await api.get(`/api/super-admin/onboarding/validate-client-id/${clientId}`);
      setClientIdValid(res.data.available);
    } catch (err) {
      setClientIdValid(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Validate client ID when it changes
    if (field === 'client_id' && !clientIdAuto) {
      validateClientId(value.toUpperCase());
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(''), 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = {
        ...formData,
        client_id: clientIdAuto ? null : formData.client_id,
        admin_password: autoGeneratePassword ? null : formData.admin_password,
        contact_phone: formData.contact_phone ? `${contactCountryCode} ${formData.contact_phone}` : '',
        admin_phone: formData.admin_phone ? `${adminCountryCode} ${formData.admin_phone}` : ''
      };

      const res = await api.post('/api/super-admin/onboarding/tenant', payload);
      setCreatedData(res.data);
      setSuccessModal(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create tenant');
    } finally {
      setLoading(false);
    }
  };

  const tierOptions = [
    { value: 'basic', label: 'Basic', description: '2 operators, 1 user included. Core scheduling & dockets.' },
    { value: 'advanced', label: 'Advanced', description: '2 operators, 1 user included. + Reports, Forms, Compliance, Timesheets, Quotes.' },
    { value: 'professional', label: 'Professional', description: '2 operators, 1 user included. + AI features, OCR, API access, Priority Support.' }
  ];

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate('/super-admin/tenants')}
          className={isDark ? 'text-slate-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Onboard New Tenant</h1>
          <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>Create a new tenant account with their first admin user</p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="bg-red-500/10 border-red-500/50">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Company Information */}
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Building2 className="h-5 w-5 text-[#ffd108]" />
              Company Information
            </CardTitle>
            <CardDescription>Basic details about the tenant company</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Company Name */}
              <div className="space-y-2">
                <Label htmlFor="company_name" className={isDark ? 'text-slate-300' : 'text-gray-700'}>
                  Company Name <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  placeholder="Acme Construction Pty Ltd"
                  className={isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}
                  required
                />
              </div>

              {/* Client ID */}
              <div className="space-y-2">
                <Label htmlFor="client_id" className={isDark ? 'text-slate-300' : 'text-gray-700'}>
                  Client ID <span className="text-red-400">*</span>
                </Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      id="client_id"
                      value={formData.client_id}
                      onChange={(e) => handleInputChange('client_id', e.target.value.toUpperCase())}
                      placeholder={clientIdAuto ? "Auto-generated" : "ABC123"}
                      className={`${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} uppercase`}
                      disabled={clientIdAuto}
                      maxLength={6}
                    />
                    {!clientIdAuto && formData.client_id && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {clientIdValid === true && <Check className="h-4 w-4 text-green-400" />}
                        {clientIdValid === false && <AlertCircle className="h-4 w-4 text-red-400" />}
                      </div>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      if (clientIdAuto) {
                        setClientIdAuto(false);
                        setFormData(prev => ({ ...prev, client_id: '' }));
                      } else {
                        setClientIdAuto(true);
                        generateClientId();
                      }
                    }}
                    className={isDark ? 'border-slate-600 hover:bg-slate-700' : 'border-gray-200 hover:bg-gray-100'}
                    title={clientIdAuto ? "Enter manually" : "Auto-generate"}
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                  {clientIdAuto ? "Will be auto-generated on creation" : "6 characters (e.g., ABC123)"}
                </p>
              </div>

              {/* Contact Email */}
              <div className="space-y-2">
                <Label htmlFor="contact_email" className={isDark ? 'text-slate-300' : 'text-gray-700'}>
                  Contact Email <span className="text-red-400">*</span>
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => handleInputChange('contact_email', e.target.value)}
                    placeholder="contact@acme.com"
                    className={`${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} pl-10`}
                    required
                  />
                </div>
              </div>

              {/* Contact Phone */}
              <div className="space-y-2">
                <Label htmlFor="contact_phone" className={isDark ? 'text-slate-300' : 'text-gray-700'}>
                  Contact Phone <span className="text-red-400">*</span>
                </Label>
                <div className="flex gap-2">
                  <Select value={contactCountryCode} onValueChange={setContactCountryCode}>
                    <SelectTrigger className={`w-[110px] flex-shrink-0 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRY_CODES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.flag} {c.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    id="contact_phone"
                    value={formData.contact_phone}
                    onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                    placeholder="400 000 000"
                    className={isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Optional Fields */}
            <div className="pt-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}">
              <p className="text-sm text-slate-400 mb-4">Optional Information</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Address */}
                <div className="space-y-2">
                  <Label htmlFor="address" className={isDark ? 'text-slate-300' : 'text-gray-700'}>Address</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      placeholder="123 Main St, Sydney"
                      className={`${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} pl-10`}
                    />
                  </div>
                </div>

                {/* ABN */}
                <div className="space-y-2">
                  <Label htmlFor="abn" className={isDark ? 'text-slate-300' : 'text-gray-700'}>ABN / Business Number</Label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="abn"
                      value={formData.abn}
                      onChange={(e) => handleInputChange('abn', e.target.value)}
                      placeholder="12 345 678 901"
                      className={`${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} pl-10`}
                    />
                  </div>
                </div>

                {/* Industry */}
                <div className="space-y-2">
                  <Label htmlFor="industry_type" className={isDark ? 'text-slate-300' : 'text-gray-700'}>Industry</Label>
                  <Select
                    value={formData.industry_type}
                    onValueChange={(v) => handleInputChange('industry_type', v)}
                  >
                    <SelectTrigger className={isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}>
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map((ind) => (
                        <SelectItem key={ind.value || ind} value={ind.value || ind}>
                          {ind.label || ind}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Tier */}
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
          <CardHeader>
            <CardTitle className={isDark ? 'text-white' : 'text-gray-900'}>Subscription Tier</CardTitle>
            <CardDescription>Select the subscription plan for this tenant</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {tierOptions.map((tier) => (
                <div
                  key={tier.value}
                  onClick={() => handleInputChange('tier', tier.value)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.tier === tier.value
                      ? 'border-[#ffd108] bg-[#ffd108]/10'
                      : 'border-slate-600 hover:border-slate-500 bg-slate-700/30'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`font-semibold ${
                      formData.tier === tier.value ? 'text-[#ffd108]' : 'text-white'
                    }`}>
                      {tier.label}
                    </span>
                    {formData.tier === tier.value && (
                      <Check className="h-5 w-5 text-[#ffd108]" />
                    )}
                  </div>
                  <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{tier.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Seats / Licenses */}
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
          <CardHeader>
            <CardTitle className={isDark ? 'text-white' : 'text-gray-900'}>Seats</CardTitle>
            <CardDescription>
              Total seats for this tenant. Includes the tier's bundled allowance (1 user + 2 mobile workers); anything above is billed as extra seats.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Users */}
              <div className="space-y-2">
                <Label htmlFor="total_users" className={isDark ? 'text-slate-300' : 'text-gray-700'}>Users (admin / dispatcher)</Label>
                <Input
                  id="total_users"
                  data-testid="total-users-input"
                  type="number"
                  min={1}
                  max={100}
                  value={formData.total_users}
                  onChange={(e) => handleInputChange('total_users', Math.max(1, parseInt(e.target.value || '1', 10)))}
                  className={isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}
                />
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                  {Math.max(0, (formData.total_users || 1) - 1)} extra above the 1 included
                </p>
              </div>
              {/* Mobile Workers */}
              <div className="space-y-2">
                <Label htmlFor="total_mobile_workers" className={isDark ? 'text-slate-300' : 'text-gray-700'}>Mobile Workers (app access)</Label>
                <Input
                  id="total_mobile_workers"
                  data-testid="total-mobile-workers-input"
                  type="number"
                  min={1}
                  max={500}
                  value={formData.total_mobile_workers}
                  onChange={(e) => handleInputChange('total_mobile_workers', Math.max(1, parseInt(e.target.value || '1', 10)))}
                  className={isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}
                />
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                  {Math.max(0, (formData.total_mobile_workers || 2) - 2)} extra above the 2 included
                </p>
              </div>
            </div>
          </CardContent>
        </Card>


        {/* Admin User */}
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <User className="h-5 w-5 text-[#ffd108]" />
              First Admin User
            </CardTitle>
            <CardDescription>Create the primary admin account for this tenant</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Admin Full Name */}
              <div className="space-y-2">
                <Label htmlFor="admin_full_name" className={isDark ? 'text-slate-300' : 'text-gray-700'}>
                  Full Name <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="admin_full_name"
                  value={formData.admin_full_name}
                  onChange={(e) => handleInputChange('admin_full_name', e.target.value)}
                  placeholder="John Smith"
                  className={isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}
                  required
                />
              </div>

              {/* Admin Email */}
              <div className="space-y-2">
                <Label htmlFor="admin_email" className={isDark ? 'text-slate-300' : 'text-gray-700'}>
                  Email <span className="text-red-400">*</span>
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="admin_email"
                    type="email"
                    value={formData.admin_email}
                    onChange={(e) => handleInputChange('admin_email', e.target.value)}
                    placeholder="john@acme.com"
                    className={`${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} pl-10`}
                    required
                  />
                </div>
              </div>

              {/* Admin Phone */}
              <div className="space-y-2">
                <Label htmlFor="admin_phone" className={isDark ? 'text-slate-300' : 'text-gray-700'}>Phone Number</Label>
                <div className="flex gap-2">
                  <Select value={adminCountryCode} onValueChange={setAdminCountryCode}>
                    <SelectTrigger className={`w-[110px] flex-shrink-0 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRY_CODES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.flag} {c.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    id="admin_phone"
                    value={formData.admin_phone}
                    onChange={(e) => handleInputChange('admin_phone', e.target.value)}
                    placeholder="400 000 000"
                    className={isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="admin_password" className={isDark ? 'text-slate-300' : 'text-gray-700'}>Password</Label>
                <div className="relative">
                  <Input
                    id="admin_password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.admin_password}
                    onChange={(e) => handleInputChange('admin_password', e.target.value)}
                    placeholder={autoGeneratePassword ? "Set by user via secure link" : "Enter password"}
                    className={`${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} pr-20`}
                    disabled={autoGeneratePassword}
                    minLength={8}
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                    {!autoGeneratePassword && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => {
                        setAutoGeneratePassword(!autoGeneratePassword);
                        if (!autoGeneratePassword) {
                          setFormData(prev => ({ ...prev, admin_password: '' }));
                        }
                      }}
                      title={autoGeneratePassword ? "Set manually" : "Auto-generate"}
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                  {autoGeneratePassword 
                    ? "The admin will set their own password via a secure activation link" 
                    : "Minimum 8 characters"}
                </p>
              </div>
            </div>

            {/* Welcome Email Option */}
            <div className="flex items-center gap-3 pt-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}">
              <Checkbox
                id="send_welcome_email"
                checked={formData.send_welcome_email}
                onCheckedChange={(checked) => handleInputChange('send_welcome_email', checked)}
              />
              <Label htmlFor="send_welcome_email" className={isDark ? 'text-slate-300 cursor-pointer' : 'text-gray-700 cursor-pointer'}>
                Send welcome email (includes a "Set Your Password" link when no password is set)
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/super-admin/tenants')}
            className={isDark ? 'border-slate-600 hover:bg-slate-700' : 'border-gray-200 hover:bg-gray-100'}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="bg-[#ffd108] text-black hover:bg-[#e6bc07]"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Create Tenant
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Success Modal */}
      <Dialog open={successModal} onOpenChange={setSuccessModal}>
        <DialogContent className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} max-w-lg`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-400">
              <CheckCircle className="h-6 w-6" />
              Tenant Created Successfully!
            </DialogTitle>
            <DialogDescription className={isDark ? 'text-slate-400' : 'text-gray-500'}>
              {createdData?.tenant?.company_name} has been onboarded.
            </DialogDescription>
          </DialogHeader>
          
          {createdData && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-900/50 rounded-lg p-4 space-y-3">
                <h4 className="font-semibold text-[#ffd108]">Login Credentials</h4>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Client ID:</span>
                    <div className="flex items-center gap-2">
                      <code className="bg-slate-700 px-2 py-1 rounded text-sm">
                        {createdData.credentials.client_id}
                      </code>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => copyToClipboard(createdData.credentials.client_id, 'client_id')}
                      >
                        {copiedField === 'client_id' ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Username:</span>
                    <div className="flex items-center gap-2">
                      <code className="bg-slate-700 px-2 py-1 rounded text-sm">
                        {createdData.credentials.username}
                      </code>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => copyToClipboard(createdData.credentials.username, 'username')}
                      >
                        {copiedField === 'username' ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  
                  {createdData.credentials.requires_activation ? (
                    <div className="flex items-center justify-between" data-testid="onboard-activation-row">
                      <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Password:</span>
                      <span className="text-sm text-[#ffd108] font-medium">Set via activation link</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Password:</span>
                      <div className="flex items-center gap-2">
                        <code className="bg-slate-700 px-2 py-1 rounded text-sm">
                          {createdData.credentials.password}
                        </code>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => copyToClipboard(createdData.credentials.password, 'password')}
                        >
                          {copiedField === 'password' ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {createdData.credentials.requires_activation && (
                  <div className="mt-3 space-y-2" data-testid="onboard-activation-panel">
                    <p className="text-xs text-slate-300">
                      {createdData.credentials.activation_email_sent
                        ? `✅ A "Set Your Password" email has been sent to ${createdData.credentials.username}. The link expires in 7 days.`
                        : `⚠️ No welcome email was sent. Share the activation link below so they can set their password (expires in 7 days).`}
                    </p>
                    {createdData.credentials.set_password_url && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        data-testid="copy-activation-link-btn"
                        onClick={() => copyToClipboard(createdData.credentials.set_password_url, 'activation')}
                      >
                        {copiedField === 'activation' ? (
                          <><Check className="h-4 w-4 mr-2 text-green-400" /> Activation Link Copied!</>
                        ) : (
                          <><Copy className="h-4 w-4 mr-2" /> Copy Set-Password Link</>
                        )}
                      </Button>
                    )}
                  </div>
                )}

                {createdData.credentials.password_auto_generated && (
                  <p className="text-xs text-amber-400 mt-2">
                    ⚠️ This password was auto-generated. Make sure to share it securely with the tenant.
                  </p>
                )}
              </div>

              <Button
                className="w-full"
                variant="outline"
                onClick={() => {
                  const creds = createdData.credentials;
                  const loginLine = 'Login URL: https://app-beta.equiprsoftware.com/login';
                  const text = creds.requires_activation
                    ? `EQUIPR CORE Account\n\nUsername: ${creds.username}\nSet your password: ${creds.set_password_url}\n\n${loginLine}`
                    : `EQUIPR CORE Login Credentials\n\nClient ID: ${creds.client_id}\nUsername: ${creds.username}\nPassword: ${creds.password}\n\n${loginLine}`;
                  copyToClipboard(text, 'all');
                }}
              >
                {copiedField === 'all' ? (
                  <><Check className="h-4 w-4 mr-2 text-green-400" /> Copied All!</>
                ) : (
                  <><Copy className="h-4 w-4 mr-2" /> Copy All Credentials</>
                )}
              </Button>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setSuccessModal(false);
                navigate('/super-admin/tenants');
              }}
              className="border-slate-600"
            >
              View All Tenants
            </Button>
            <Button
              onClick={() => {
                setSuccessModal(false);
                navigate(`/super-admin/tenants/${createdData?.tenant?.client_id}`);
              }}
              className="bg-[#ffd108] text-black hover:bg-[#e6bc07]"
            >
              View Tenant Details
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantOnboarding;
